import { useQuery, useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import filterContainerSelector from '@store/filterContainer/selectors';
import { pushDrilldownFilter } from '@store/dimensionContainer/actions';
import { resetDrillDown } from '@store/filterContainer/actions';
import { getFirstAppliedFilter, featureSelector } from '@store/common/selectors';
import * as commonTypes from '@store/common/types';
import * as filterTypes from '@store/filterContainer/types';
import * as apis from '@src/api/filters';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { DataSource, type SavedFilter, type Filter, type FilterMeta } from '../components';

const modes = {
  [DataSource.video]: 'ei',
  [DataSource.ads]: 'ai',
  [DataSource.app]: 'app',
};

interface QueryOptions {
  enabled?: boolean;
}
export const useFilterMeta = (dataSource: DataSource, options: QueryOptions) => {
  return useQuery(
    ['get-filter-meta', dataSource],
    () => {
      const params = { mode: modes[dataSource] };
      if (dataSource === DataSource.app) {
        return apis.getAppMeta<FilterMeta>({ params });
      }
      return apis.getMeta<FilterMeta>({ params, ...apis.getV2AxiosConfig() });
    },
    {
      refetchOnWindowFocus: false,
      enabled: options.enabled,
    },
  );
};

export const useGetSavedFilters = (dataSource: DataSource, options: QueryOptions) => {
  return useQuery(
    ['get-saved-filters', dataSource],
    () => {
      const params = { mode: modes[dataSource] };
      if (dataSource === DataSource.app) {
        return apis.getFiltersForAppExperience<SavedFilter[]>({ params });
      }
      return apis.getFilters<SavedFilter[]>({ params, ...apis.getV2AxiosConfig() });
    },
    {
      refetchOnWindowFocus: false,
      enabled: options.enabled,
    },
  );
};

export const useEditFilter = () => {
  return useMutation(
    ({ id, filter, dataSource }: { id: number; filter: Filter; dataSource: DataSource }) => {
      const params = { mode: modes[dataSource] };
      if (dataSource === DataSource.app) {
        return apis.updateFilterForAppExperience(id, filter, { params });
      }
      return apis.updateFilter(id, filter, { params, ...apis.getV2AxiosConfig() });
    },
  );
};

export const useEnableFilter = () => {
  return useMutation(({ id, dataSource }: { id: number; dataSource: DataSource }) => {
    const params = { mode: modes[dataSource] };
    return apis.enableFilter(id, {
      params,
      ...apis.getV2AxiosConfig(),
    });
  });
};

export const useCreateFilter = () => {
  return useMutation(({ filter, dataSource }: { filter: Filter; dataSource: DataSource }) => {
    const params = { mode: modes[dataSource] };
    if (dataSource === DataSource.app) {
      return apis.createFilterForAppExperience<{ id: number; filter: SavedFilter }>(filter, {
        params,
      });
    }
    return apis.createFilter<{ id: number; filter: SavedFilter }>(filter, {
      params,
      ...apis.getV2AxiosConfig(),
    });
  });
};

export const useDeleteFilter = () => {
  return useMutation(({ id, dataSource }: { id: number; dataSource: DataSource }) => {
    const params = { mode: modes[dataSource] };
    if (dataSource === DataSource.app) {
      return apis.deleteFilterForAppExperience(id, { params });
    }
    return apis.deleteFilter(id, { params, ...apis.getV2AxiosConfig() });
  });
};

export const useFeatureFlags = (dataSource: DataSource) => {
  const isApp = dataSource === DataSource.app;
  // check if app experience is enabled
  const appEnabled = useSelector(featureSelector('app-experience')) as boolean;
  // controll the maximum count of rules that can be created
  const drilldownLimit = useSelector(
    featureSelector(isApp ? 'app-drilldown-limit' : 'drilldown-level'),
  ) as number;
  // controll if allow user to create & clone filter
  const allowCreateFilter = useSelector(
    featureSelector(isApp ? 'app-create-filter' : 'create-filter'),
  ) as boolean;

  return {
    appEnabled,
    drilldownLimit,
    allowCreateFilter,
  };
};

const checkShouldFilterPopupUpdated = (dataSource: DataSource) => {
  const isVideoOrAdsFilterUpdated =
    ['EI', 'AI'].includes(CURRENT_PRODUCT_MODE) &&
    [DataSource.ads, DataSource.video].includes(dataSource);
  const isAppFilterUpdated = CURRENT_PRODUCT_MODE === 'APP' && dataSource === 'app';
  const shouldFilterPopupUpdated = isVideoOrAdsFilterUpdated || isAppFilterUpdated;

  return shouldFilterPopupUpdated;
};

// TODO: will be deprecated after filter popup is replaced by new filter search box
export const useNotifyFilterPopup = () => {
  const dispatch = useDispatch();
  const filterContainerState = useSelector(filterContainerSelector);
  const appliedFilter = useSelector(getFirstAppliedFilter);

  return {
    onCreateNotify: (dataSource: DataSource, filter: SavedFilter) => {
      if (!checkShouldFilterPopupUpdated(dataSource)) return;
      try {
        // check if the filter is valid
        if (!filter || ['fid', 'category', 'name', 'rule'].some((key) => !filter[key])) return;
        const filters = get(filterContainerState, 'filters', []);
        const newFilters = [filter, ...filters];
        dispatch({
          type: filterTypes.DONE_GETTING_FILTERS,
          filters: newFilters,
        });
      } catch {
        /**/
      }
    },
    onDeleteNotify: (id: number, dataSource: DataSource) => {
      if (!checkShouldFilterPopupUpdated(dataSource)) return;

      try {
        const filters = get(filterContainerState, 'filters', []);
        if (!filters && !filters.length) return;

        const updatedFilters = filters.filter((filter) => filter.fid !== id);
        dispatch({
          type: filterTypes.DONE_GETTING_FILTERS,
          filters: updatedFilters,
        });
      } catch {
        /**/
      }
    },
    onUpdateNotify: (id: number, filter: Filter, dataSource: DataSource) => {
      if (!checkShouldFilterPopupUpdated(dataSource)) return;

      try {
        const filters = get(filterContainerState, 'filters', []);
        if (!filters && !filters.length) return;
        const newFilters = [...filters];
        const targetFilter = newFilters.find((it) => it.fid === id);

        if (targetFilter) {
          targetFilter.category = `${filter.category}/${filter.subcategory}`;
          targetFilter.name = filter.name;
          targetFilter.rule = filter.rule;
          dispatch({
            type: filterTypes.DONE_GETTING_FILTERS,
            filters: newFilters,
          });
        }

        const isApplied = appliedFilter.fid === id;
        if (isApplied) {
          const tRules = getRulesFromFilter(filter.rule, true);
          const tFilter = getFilterFromRules(tRules, true);
          dispatch({
            type: commonTypes.APPLY_FILTER,
            filter: {
              name: filter.name,
              rule: filter.rule,
              fid: id,
            },
          });
          dispatch(resetDrillDown());
          dispatch(pushDrilldownFilter(filter.name || tFilter.name, tFilter.rule));
        }
      } catch (e) {
        /**/
      }
    },
  };
};
